import { useEffect, useRef, useState } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, PerspectiveCamera } from '@react-three/drei';
import Lenis from '@studio-freight/lenis';
import * as THREE from 'three';
import {
  Sparkles,
  Rocket,
  Zap,
  Star,
  Globe,
  Mail,
  Twitter,
  Linkedin,
  Github,
  ArrowRight,
  Calendar,
  Users,
  Target
} from 'lucide-react';

// Animated floating particles in 3D space
function FloatingParticles() {
  const particlesRef = useRef<THREE.Points>(null);
  const count = 500;

  const positions = new Float32Array(count * 3);
  const colors = new Float32Array(count * 3);

  for (let i = 0; i < count; i++) {
    positions[i * 3] = (Math.random() - 0.5) * 20;
    positions[i * 3 + 1] = (Math.random() - 0.5) * 20;
    positions[i * 3 + 2] = (Math.random() - 0.5) * 20;

    const color = new THREE.Color();
    const colors_palette = ['#14b8a6', '#06b6d4', '#a855f7', '#3b82f6'];
    color.set(colors_palette[Math.floor(Math.random() * colors_palette.length)]);

    colors[i * 3] = color.r;
    colors[i * 3 + 1] = color.g;
    colors[i * 3 + 2] = color.b;
  }

  useFrame((state) => {
    if (particlesRef.current) {
      particlesRef.current.rotation.y = state.clock.elapsedTime * 0.05;
      particlesRef.current.rotation.x = Math.sin(state.clock.elapsedTime * 0.1) * 0.1;
    }
  });

  return (
    <points ref={particlesRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={count}
          array={positions}
          itemSize={3}
        />
        <bufferAttribute
          attach="attributes-color"
          count={count}
          array={colors}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial
        size={0.05}
        vertexColors
        transparent
        opacity={0.6}
        sizeAttenuation
        blending={THREE.AdditiveBlending}
      />
    </points>
  );
}

// Rotating hexagonal grid
function HexagonalGrid() {
  const groupRef = useRef<THREE.Group>(null);

  useFrame((state) => {
    if (groupRef.current) {
      groupRef.current.rotation.z = state.clock.elapsedTime * 0.1;
      groupRef.current.rotation.y = Math.sin(state.clock.elapsedTime * 0.2) * 0.2;
    }
  });

  const hexagons = [];
  const radius = 0.5;
  const rows = 5;
  const cols = 5;

  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const x = col * (radius * 2 * Math.cos(Math.PI / 6)) + (row % 2) * radius * Math.cos(Math.PI / 6);
      const y = row * (radius * 1.5);

      hexagons.push(
        <mesh key={`hex-${row}-${col}`} position={[x - rows, y - cols, 0]}>
          <cylinderGeometry args={[radius * 0.9, radius * 0.9, 0.1, 6]} />
          <meshStandardMaterial
            color={row % 2 === col % 2 ? '#14b8a6' : '#06b6d4'}
            transparent
            opacity={0.3}
            wireframe
          />
        </mesh>
      );
    }
  }

  return <group ref={groupRef}>{hexagons}</group>;
}

// Orbiting spheres
function OrbitingSpheres() {
  const groupRef = useRef<THREE.Group>(null);

  useFrame((state) => {
    if (groupRef.current) {
      groupRef.current.rotation.y = state.clock.elapsedTime * 0.3;
    }
  });

  const spheres = [
    { radius: 3, color: '#14b8a6', size: 0.2 },
    { radius: 4, color: '#06b6d4', size: 0.15 },
    { radius: 5, color: '#a855f7', size: 0.18 },
    { radius: 6, color: '#3b82f6', size: 0.12 },
  ];

  return (
    <group ref={groupRef}>
      {spheres.map((sphere, i) => {
        const angle = (i / spheres.length) * Math.PI * 2;
        return (
          <mesh
            key={i}
            position={[
              Math.cos(angle) * sphere.radius,
              Math.sin(angle + Date.now() / 1000) * 0.5,
              Math.sin(angle) * sphere.radius
            ]}
          >
            <sphereGeometry args={[sphere.size, 16, 16]} />
            <meshStandardMaterial
              color={sphere.color}
              emissive={sphere.color}
              emissiveIntensity={0.5}
            />
          </mesh>
        );
      })}
    </group>
  );
}

// DNA Helix structure
function DNAHelix() {
  const groupRef = useRef<THREE.Group>(null);

  useFrame((state) => {
    if (groupRef.current) {
      groupRef.current.rotation.y = state.clock.elapsedTime * 0.2;
    }
  });

  const pairs = 20;
  const helixElements = [];

  for (let i = 0; i < pairs; i++) {
    const t = i / pairs;
    const angle1 = t * Math.PI * 4;
    const angle2 = angle1 + Math.PI;
    const y = t * 6 - 3;
    const radius = 1.5;

    // Strand 1
    helixElements.push(
      <mesh key={`s1-${i}`} position={[Math.cos(angle1) * radius, y, Math.sin(angle1) * radius]}>
        <sphereGeometry args={[0.1, 8, 8]} />
        <meshStandardMaterial color="#14b8a6" emissive="#14b8a6" emissiveIntensity={0.5} />
      </mesh>
    );

    // Strand 2
    helixElements.push(
      <mesh key={`s2-${i}`} position={[Math.cos(angle2) * radius, y, Math.sin(angle2) * radius]}>
        <sphereGeometry args={[0.1, 8, 8]} />
        <meshStandardMaterial color="#06b6d4" emissive="#06b6d4" emissiveIntensity={0.5} />
      </mesh>
    );

    // Connecting bar
    const midPoint = new THREE.Vector3(
      (Math.cos(angle1) + Math.cos(angle2)) * radius / 2,
      y,
      (Math.sin(angle1) + Math.sin(angle2)) * radius / 2
    );

    helixElements.push(
      <mesh key={`bar-${i}`} position={[midPoint.x, midPoint.y, midPoint.z]}>
        <cylinderGeometry args={[0.02, 0.02, radius * 2, 8]} />
        <meshStandardMaterial color="#a855f7" transparent opacity={0.5} />
      </mesh>
    );
  }

  return <group ref={groupRef}>{helixElements}</group>;
}

export const ComingSoon = () => {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [timeLeft, setTimeLeft] = useState({
    days: 45,
    hours: 12,
    minutes: 34,
    seconds: 56
  });

  // Lenis smooth scroll
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: 'vertical',
      smoothWheel: true,
      touchMultiplier: 2,
    } as ConstructorParameters<typeof Lenis>[0]);

    function raf(time: number) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
    };
  }, []);

  // Countdown timer
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) {
          return { ...prev, seconds: prev.seconds - 1 };
        } else if (prev.minutes > 0) {
          return { ...prev, minutes: prev.minutes - 1, seconds: 59 };
        } else if (prev.hours > 0) {
          return { ...prev, hours: prev.hours - 1, minutes: 59, seconds: 59 };
        } else if (prev.days > 0) {
          return { ...prev, days: prev.days - 1, hours: 23, minutes: 59, seconds: 59 };
        }
        return prev;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    setSubscribed(true);
    setTimeout(() => setSubscribed(false), 3000);
  };

  return (
    <div className="min-h-screen bg-[#0d0d12] text-white overflow-x-hidden">
      {/* Hero Section with 3D Background */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* 3D Canvas Background */}
        <div className="absolute inset-0 opacity-30">
          <Canvas>
            <PerspectiveCamera makeDefault position={[0, 0, 10]} />
            <ambientLight intensity={0.5} />
            <pointLight position={[10, 10, 10]} intensity={1} />
            <FloatingParticles />
            <OrbitingSpheres />
            <OrbitControls enableZoom={false} enablePan={false} />
          </Canvas>
        </div>

        {/* Content */}
        <div className="relative z-10 text-center px-4 max-w-5xl mx-auto">
          {/* Animated badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/[0.04] border border-teal-500/30 rounded-full mb-8 animate-fadeIn backdrop-blur-sm">
            <Sparkles className="w-4 h-4 text-teal-400 animate-pulse" />
            <span className="text-sm font-semibold bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">
              Something Amazing is Coming
            </span>
            <Sparkles className="w-4 h-4 text-cyan-400 animate-pulse" style={{ animationDelay: '0.5s' }} />
          </div>

          {/* Main heading */}
          <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black mb-6 animate-fadeIn" style={{ animationDelay: '0.1s' }}>
            <span className="bg-gradient-to-r from-teal-400 via-cyan-400 to-purple-400 bg-clip-text text-transparent">
              Launching
            </span>
            <br />
            <span className="text-white">Very Soon</span>
          </h1>

          <p className="text-xl md:text-2xl text-gray-400 mb-12 max-w-2xl mx-auto leading-relaxed animate-fadeIn" style={{ animationDelay: '0.2s' }}>
            We're crafting something extraordinary. A revolutionary platform that will transform
            the way you experience online education and assessments.
          </p>

          {/* Countdown Timer */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12 max-w-3xl mx-auto animate-fadeIn" style={{ animationDelay: '0.3s' }}>
            {[
              { label: 'Days', value: timeLeft.days },
              { label: 'Hours', value: timeLeft.hours },
              { label: 'Minutes', value: timeLeft.minutes },
              { label: 'Seconds', value: timeLeft.seconds },
            ].map((item) => (
              <div
                key={item.label}
                className="bg-white/[0.04] backdrop-blur-sm border border-white/[0.06] rounded-2xl p-6 hover:border-teal-500/30 transition-all hover:scale-105"
              >
                <div className="text-4xl md:text-5xl font-black bg-gradient-to-br from-teal-400 to-cyan-400 bg-clip-text text-transparent mb-2">
                  {String(item.value).padStart(2, '0')}
                </div>
                <div className="text-sm text-gray-500 uppercase tracking-wider">{item.label}</div>
              </div>
            ))}
          </div>

          {/* Email Subscribe Form */}
          <form onSubmit={handleSubscribe} className="max-w-md mx-auto animate-fadeIn" style={{ animationDelay: '0.4s' }}>
            <div className="flex gap-3">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                className="flex-1 px-6 py-4 bg-white/[0.04] border border-white/[0.06] rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-teal-500/50 focus:ring-2 focus:ring-teal-500/20 transition-all"
              />
              <button
                type="submit"
                className="px-8 py-4 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-xl font-semibold hover:from-teal-600 hover:to-cyan-600 transition-all hover:scale-105 shadow-lg shadow-teal-500/20 flex items-center gap-2"
              >
                {subscribed ? (
                  <>
                    <span className="text-sm">✓ Subscribed!</span>
                  </>
                ) : (
                  <>
                    <span>Notify Me</span>
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </div>
          </form>

          <p className="text-sm text-gray-500 mt-4">
            Join 10,000+ early adopters waiting for the launch
          </p>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/20 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-teal-400 rounded-full animate-pulse" />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative py-32 px-4">
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <Canvas>
            <PerspectiveCamera makeDefault position={[0, 0, 8]} />
            <ambientLight intensity={0.5} />
            <pointLight position={[10, 10, 10]} />
            <HexagonalGrid />
            <OrbitControls enableZoom={false} enablePan={false} />
          </Canvas>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-black mb-6">
              <span className="bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">
                What's Coming
              </span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Powerful features designed to revolutionize online learning and assessment
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: Rocket,
                title: 'Lightning Fast',
                description: 'Optimized performance for seamless user experience',
                gradient: 'from-teal-500 to-cyan-500'
              },
              {
                icon: Zap,
                title: 'AI-Powered',
                description: 'Smart algorithms for personalized learning paths',
                gradient: 'from-cyan-500 to-blue-500'
              },
              {
                icon: Globe,
                title: 'Global Access',
                description: 'Available anywhere, anytime, on any device',
                gradient: 'from-blue-500 to-purple-500'
              },
              {
                icon: Target,
                title: 'Results Driven',
                description: 'Proven methods to achieve learning goals faster',
                gradient: 'from-purple-500 to-pink-500'
              },
            ].map((feature, i) => (
              <div
                key={i}
                className="group bg-white/[0.02] backdrop-blur-sm border border-white/[0.06] rounded-2xl p-8 hover:border-teal-500/30 transition-all hover:scale-105 hover:-translate-y-2"
                style={{ animationDelay: `${i * 0.1}s` }}
              >
                <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                  <feature.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                <p className="text-gray-400 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="relative py-20 px-4 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { icon: Users, label: 'Early Subscribers', value: '10K+' },
              { icon: Globe, label: 'Countries Ready', value: '50+' },
              { icon: Star, label: 'Beta Testers', value: '500+' },
              { icon: Calendar, label: 'Launch Date', value: 'Q1 2025' },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <stat.icon className="w-10 h-10 mx-auto mb-4 text-teal-400" />
                <div className="text-3xl md:text-4xl font-black bg-gradient-to-br from-teal-400 to-cyan-400 bg-clip-text text-transparent mb-2">
                  {stat.value}
                </div>
                <div className="text-sm text-gray-500">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DNA Helix Section */}
      <section className="relative py-32 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <Canvas>
            <PerspectiveCamera makeDefault position={[0, 0, 10]} />
            <ambientLight intensity={0.5} />
            <pointLight position={[10, 10, 10]} />
            <DNAHelix />
            <OrbitControls enableZoom={false} enablePan={false} />
          </Canvas>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-black mb-6">
            <span className="bg-gradient-to-r from-teal-400 via-cyan-400 to-purple-400 bg-clip-text text-transparent">
              Built with Precision
            </span>
          </h2>
          <p className="text-xl text-gray-400 mb-12 leading-relaxed">
            Every line of code, every design decision, every feature is crafted with meticulous attention
            to detail. We're not just building a platform—we're engineering excellence.
          </p>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { value: '99.9%', label: 'Uptime SLA' },
              { value: '<100ms', label: 'Response Time' },
              { value: 'A+', label: 'Security Grade' },
            ].map((metric, i) => (
              <div key={i} className="bg-white/[0.04] backdrop-blur-sm border border-white/[0.06] rounded-xl p-6">
                <div className="text-3xl font-black text-teal-400 mb-2">{metric.value}</div>
                <div className="text-sm text-gray-500">{metric.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="relative py-32 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <Rocket className="w-20 h-20 mx-auto mb-8 text-teal-400 animate-bounce" />
          <h2 className="text-4xl md:text-5xl font-black mb-6">
            Ready to Experience the Future?
          </h2>
          <p className="text-xl text-gray-400 mb-12 leading-relaxed">
            Be among the first to access our revolutionary platform when we launch
          </p>

          <form onSubmit={handleSubscribe} className="max-w-md mx-auto mb-12">
            <div className="flex gap-3">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                className="flex-1 px-6 py-4 bg-white/[0.04] border border-white/[0.06] rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-teal-500/50 focus:ring-2 focus:ring-teal-500/20 transition-all"
              />
              <button
                type="submit"
                className="px-8 py-4 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-xl font-semibold hover:from-teal-600 hover:to-cyan-600 transition-all hover:scale-105 shadow-lg shadow-teal-500/20"
              >
                {subscribed ? '✓ Done!' : 'Join Waitlist'}
              </button>
            </div>
          </form>

          {/* Social Links */}
          <div className="flex items-center justify-center gap-4">
            <a href="#" className="w-12 h-12 bg-white/[0.04] border border-white/[0.06] rounded-xl flex items-center justify-center hover:border-teal-500/30 hover:scale-110 transition-all">
              <Twitter className="w-5 h-5 text-gray-400 hover:text-teal-400" />
            </a>
            <a href="#" className="w-12 h-12 bg-white/[0.04] border border-white/[0.06] rounded-xl flex items-center justify-center hover:border-teal-500/30 hover:scale-110 transition-all">
              <Linkedin className="w-5 h-5 text-gray-400 hover:text-teal-400" />
            </a>
            <a href="#" className="w-12 h-12 bg-white/[0.04] border border-white/[0.06] rounded-xl flex items-center justify-center hover:border-teal-500/30 hover:scale-110 transition-all">
              <Github className="w-5 h-5 text-gray-400 hover:text-teal-400" />
            </a>
            <a href="#" className="w-12 h-12 bg-white/[0.04] border border-white/[0.06] rounded-xl flex items-center justify-center hover:border-teal-500/30 hover:scale-110 transition-all">
              <Mail className="w-5 h-5 text-gray-400 hover:text-teal-400" />
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/[0.06] py-8 px-4">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-teal-400" />
            <span className="font-bold text-lg">CyberFort</span>
          </div>
          <p className="text-sm text-gray-500">
            © 2025 CyberFort. All rights reserved. Coming Soon.
          </p>
        </div>
      </footer>

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.8s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default ComingSoon;
